<template lang="pug">
v-bottom-sheet(v-model="miniAppBottomSheet" fullscreen persistent content-class="re-miniapp-dialog")
  v-sheet
    iframe.re-fullscreen-iframe(ref="miniAppIframe" :src="miniAppIframeSrc" :allow="miniAppIframeAllow")
</template>

<script>
import { mapActions, mapState } from 'vuex'
import $http from '../../utils/http'
import { isDeepLink } from '../../utils/deeplink'
import { getLocale } from '../../i18n'

export default {
  name: 'MiniApp',

  data() {
    return {
      miniAppBottomSheet: false,
      miniAppIframeSrc: null,
      miniAppIAB: null,
      miniAppTimerId: null,
      savedScrollY: 0,
      savedPath: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      cashRegister: state => state.cashRegisters.cashRegister.cashRegister,
      organization: state => state.cashRegisters.cashRegister.organization,
      visible: state => state.miniapp.visible,
      app: state => state.miniapp.app,
      helpCrunchUnreadCount: state => state.tools.helpCrunchUnreadCount,
    }),

    miniAppIframeAllow() {
      try {
        const { origin } = new URL(this.app.url)
        return `microphone; loopback-network ${origin}; local-network ${origin}; local-network-access ${origin}`
      } catch (e) {
        return 'microphone'
      }
    },
  },

  watch: {
    visible(value) {
      if (value) {
        this.open()
      }
    },

    // Страховка от десинка: если шторку закрыл сам Vuetify (click-outside, ESC
    // и т.п.), store.visible оставался true и watcher выше больше никогда не
    // срабатывал - миниаппы "умирали" до перезагрузки
    miniAppBottomSheet(value) {
      if (!value && this.visible) {
        this.close()
      }

      // Собственный замок скролла: vuetify-овский overflow-y-hidden глобальный
      // и без ref-counting - его снимает showScroll() любого другого диалога
      // (например, при открытии миниаппа с /settings хвост навигации
      // перемонтирует диалоги и скроллбар возвращался под iframe)
      if (value) {
        this.savedScrollY = window.scrollY
        this.savedPath = this.$route.path
        document.documentElement.classList.add('re-miniapp-open')
      } else {
        document.documentElement.classList.remove('re-miniapp-open')
        // Восстанавливаем позицию, только если страница не менялась под шторкой
        window.scrollTo(0, this.$route.path === this.savedPath ? this.savedScrollY : 0)
      }
    },
  },

  beforeDestroy() {
    document.documentElement.classList.remove('re-miniapp-open')
  },

  methods: {
    ...mapActions({
      closeMiniApp: 'miniapp/close',
      init: 'cashRegisters/init',
      clearPositions: 'positions/clear',
      setGlobalLoading: 'tools/setGlobalLoading',
      analyticsLogEvent: 'analytics/logEvent',
      copyToClipboard: 'tools/copyToClipboard',
      showSnackbar: 'tools/showSnackbar',
      fetchUnreadMessagesCount: 'tools/fetchUnreadMessagesCount',
    }),

    open() {
      const url = new URL(this.app.url)
      url.searchParams.append('themeDark', this.$vuetify.theme.dark)
      url.searchParams.append('phone', this.user.phone)
      url.searchParams.append('lang', getLocale())
      url.searchParams.append('isApp', this.$isCordova())
      url.searchParams.append('iinBin', this.organization.businessId)
      url.searchParams.append('cashRegisterId', this.cashRegister.id)

      // Cache busting через query-параметр
      url.searchParams.append('cacheBusting', Date.now())

      console.debug('MiniApp params: ', this.app.params)

      if (this.app.params && this.app.params.length > 0) {
        this.app.params.forEach(item => {
          const [key, value] = Object.entries(item)[0]
          url.searchParams.append(key, value)
        })
      }

      if (this.$isCordova()) {
        this.miniAppIAB = undefined
        this.miniAppIAB = window.cordova.InAppBrowser.open(url.toString(), '_blank', 'location=no,toolbar=no,hidenavigationbuttons=yes,hideurlbar=yes,zoom=no,enableViewportScale=no,hardwareback=yes')
        this.miniAppIAB.addEventListener('message', this.onMessage)
        this.miniAppIAB.addEventListener('exit', this.onExit)
      } else {
        window.addEventListener('message', this.onMessage, false)
        this.miniAppIframeSrc = url.toString()
        this.miniAppBottomSheet = true
      }
    },

    onMessage(event) {
      // Миниапп мог быть уже закрыт (app.url обнулён), а сообщения от iframe ещё приходят
      if (!this.$isCordova() && !this.app.url) {
        return
      }
      if (event.data && (this.$isCordova() || this.checkOrigin(event.origin, this.app.url))) {
        console.debug(event)

        try {
          const { id, method, param } = this.$isCordova() ? event.data : JSON.parse(event.data)

          // loaded
          if (method === 'loaded') {
            if (this.miniAppTimerId) clearTimeout(this.miniAppTimerId)
            this.postMessage(id)
            if (!this.$isCordova()) {
              this.$refs.miniAppIframe.focus()
            }
          }

          // urlParams
          if (method === 'urlParams') {
            const urlParams = {
              phone: this.user.phone,
              lang: getLocale(),
              isApp: this.$isCordova(),
            }
            urlParams.iinBin = this.organization.businessId
            urlParams.cashRegisterId = this.cashRegister.id
            urlParams.organizationName = this.organization.businessName
            this.postMessage(id, urlParams)
          }

          // close
          if (method === 'close') {
            if (this.miniAppTimerId) clearTimeout(this.miniAppTimerId)

            this.close()
            this.postMessage(id)

            // Перезагружаем список касс. Передаём id текущей кассы: если миниапп
            // снял её с учёта (mode: dereg) и она ушла в архив, init оставит её
            // выбранной, а не подменит первой рабочей (CORE-794)
            if (param && param.refresh === true) {
              this.setGlobalLoading(true)
              this.init({ params: { cashRegisterId: this.cashRegister.id } }).then(() => {
                this.setGlobalLoading(false)
              })
            }

            // Перезагружаем список касс и выбираем кассу
            if (param && param.select) {
              this.setGlobalLoading(true)
              this.init({ params: { cashRegisterId: param.select } })
                .then(() => {
                  this.clearPositions()
                  this.setGlobalLoading(false)
                })
            }
          }

          // customToken
          if (method === 'customToken') {
            $http.post(`/api/auth/app/login?app=${this.app.name}`).then((response) => {
              this.postMessage(id, response.data)
            }).catch((error) => {
              console.error(error)
            })
          }

          // barcode
          if (method === 'barcode') {
            const aspectRatio = parseInt(localStorage.getItem('rekassa.kz-ui-camera-aspectRatio') || 1, 10)
            window.cordova.plugins.rekassaBarcode.scan((result) => {
              this.analyticsLogEvent({ eventName: 're_barcode_scanned' })
              this.postMessage(id, `${result}`)
            },
            () => {
            }, { aspectRatio })
          }

          // analytics
          if (method === 'analytics') {
            // Проверяем, является ли param строкой или объектом
            if (typeof param === 'string') {
              // Старый формат, когда param — строка
              this.analyticsLogEvent({ eventName: param })
            } else if (typeof param === 'object' && param !== null) {
              // Новый формат, когда param — объект с key-value
              this.analyticsLogEvent({ eventName: param.eventName, eventProperties: param.eventProperties })
            }
            this.postMessage(id)
          }

          // href
          if (method === 'href') {
            if (this.$isCordova()) {
              if (isDeepLink(param)) {
                this.$openLink(param, '_system')
                this.close()
              } else {
                this.miniAppIAB = this.$openLink(param, '_system')
                this.miniAppIAB.addEventListener('message', this.onMessage)
                this.miniAppIAB.addEventListener('exit', this.onExit)
              }
            } else {
              this.$openLink(param, '_system')

              if (isDeepLink(param)) {
                console.log('close')
                this.close()
              }
            }

            this.postMessage(id)
          }

          // share
          if (method === 'share') {
            if (this.$isCordova()) {
              window.plugins.socialsharing.shareW3C({
                title: this.$t('отправить_ссылку'),
                text: param,
              })
            } else {
              this.copyToClipboard(param)
            }
            this.postMessage(id)
          }

          // download
          if (method === 'download') {
            if (this.$isCordova()) {
              window.canvas2ImagePlugin.shareFileData(
                () => {
                  console.debug('File shared successfully')
                },
                (error) => {
                  console.error('Error sharing file:', error)
                },
                param.base64File.split(',')[1],
                param.fileName,
              )
            } else {
              const a = document.createElement('a')
              a.href = this.makeOctetStreamForMobileSafari(param.base64File)
              a.download = param.fileName
              a.click()
            }
            this.postMessage(id)
          }

          // clipboard
          if (method === 'clipboard') {
            this.copyToClipboard(param)
            this.postMessage(id)
          }

          // appInstalled
          if (method === 'appInstalled' && this.$isCordova()) {
            if (param === 'egov') {
              window.cordova.plugins.rekassaAppavailability.hasEgovMobile((result) => {
                this.postMessage(id, result)
              }, () => {})
            } else if (param === 'egovBusiness') {
              window.cordova.plugins.rekassaAppavailability.hasEgovBusiness((result) => {
                this.postMessage(id, result)
              }, () => {})
            }
          }

          // route
          if (method === 'route') {
            this.close()
            // Костыль для iOS
            if (this.$isCordova() && !this.$isAndroid()) {
              setTimeout(() => {
                this.$router.push(param)
              }, 1000)
            } else {
              this.$router.push(param)
            }
            this.postMessage(id)
          }

          // helpCrunch
          if (method === 'helpCrunch') {
            if (param === 'open') {
              this.$showHelpCrunch()
              this.postMessage(id)
            }
            if (param === 'unreadCount') {
              this.postMessage(id, this.helpCrunchUnreadCount)
            }
          }

          // messages
          if (method === 'messages') {
            if (param === 'fetchUnreadCount') {
              this.fetchUnreadMessagesCount()
              this.postMessage(id)
            }
          }
        } catch (error) {
          // console.error(error)
        }
      }
    },

    onExit() {
      this.miniAppIAB.removeEventListener('message', this.onMessage)
      this.miniAppIAB = undefined
      this.closeMiniApp()
    },

    close() {
      if (this.$isCordova()) {
        this.miniAppIAB.close()
      } else {
        this.miniAppBottomSheet = false
        this.miniAppIframeSrc = null
        window.removeEventListener('message', this.onMessage, false)
      }
      this.closeMiniApp()
    },

    checkOrigin(origin, url) {
      try {
        const parsedUrl = new URL(url)
        return parsedUrl.origin === origin
      } catch (e) {
        console.error('Invalid URL:', e)
        return false
      }
    },

    postMessage(id, result) {
      // Миниапп мог быть уже закрыт, пока ждали ответ (customToken и т.п.)
      if (this.$isCordova()) {
        if (!this.miniAppIAB) {
          return
        }
        this.miniAppIAB.executeScript({ code: `window.onPostMessage('${id}', '${JSON.stringify(result)}');` })
      } else {
        if (!this.$refs.miniAppIframe || !this.app.url) {
          return
        }
        this.$refs.miniAppIframe.contentWindow.postMessage(JSON.stringify({ id, result }), this.app.url)
      }
    },

    /**
     * Mark application/pdf data URIs on mobile Safari as application/octet-stream to trigger downloads.
     *
     * Mobile Safari treats PDF documents in a special way, which can result
     * in data URIs for PDF documents failing to either get opened or get downloaded.
     * This method tricks mobile Safari into triggering a download.
     *
     * @param dataURI
     */
    makeOctetStreamForMobileSafari(dataURI) {
      const isIOS = /iP(ad|hone|od)/.test(navigator.userAgent)
      const isWebKit = /WebKit/.test(navigator.userAgent)
      const isChrome = /CriOS/.test(navigator.userAgent)
      const isFirefox = /FxiOS/.test(navigator.userAgent)

      const isMobileSafari = isIOS && isWebKit && !isChrome && !isFirefox
      const isPDF = dataURI.startsWith('data:application/pdf')

      if (
        !isMobileSafari
        || !isPDF
      ) {
        // Skip if not mobile Safari or if not a PDF
        return dataURI
      }

      return dataURI.replace(
        /^data:application\/pdf;/,
        'data:application/octet-stream;',
      )
    },

  },

}
</script>

<style lang="stylus">
</style>
